import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FiAlertCircle, FiArrowRight } from 'react-icons/fi';
import AccountTypeToggle from '../components/signup/AccountTypeToggle';
import PlanPicker from '../components/signup/PlanPicker';
import RegistrationForm, { validateRegistrationForm } from '../components/signup/RegistrationForm';
import CheckoutModal from '../components/signup/CheckoutModal';
import SuccessScreen from '../components/signup/SuccessScreen';
import { getPackages, verifyRegistration } from '../lib/api';

const EMPTY_VALUES = {
  fullname: '',
  orgName: '',
  orgEmail: '',
  username: '',
  email: '',
  password: '',
  confirmPassword: '',
};

export default function SignupPage() {
  const [searchParams] = useSearchParams();
  const initialType = searchParams.get('type') === 'organization' ? 'organization' : 'individual';

  const [accountType, setAccountType] = useState(initialType);
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [values, setValues] = useState(EMPTY_VALUES);
  const [errors, setErrors] = useState({});
  const [usernameValid, setUsernameValid] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [result, setResult] = useState(null);
  const [submitAttempted, setSubmitAttempted] = useState(false);

  useEffect(() => {
    let active = true;
    getPackages(accountType).then((data) => {
      if (!active) return;
      setPackages(data);
      const preselectId = Number(searchParams.get('package'));
      const match = data.find((p) => p.Id === preselectId) || data[0] || null;
      setSelectedPackage(match);
    });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountType]);

  const handleAccountTypeChange = (type) => {
    setAccountType(type);
    setValues(EMPTY_VALUES);
    setErrors({});
    setSubmitAttempted(false);
  };

  const handleFieldChange = (field, value) => {
    setValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleUsernameValidity = (field, valid) => {
    if (field === 'username') setUsernameValid(valid);
  };

  const passwordsMatch = values.password && values.password === values.confirmPassword;

  const canContinue = useMemo(
    () => selectedPackage && usernameValid && passwordsMatch,
    [selectedPackage, usernameValid, passwordsMatch]
  );

  const handleContinue = () => {
    setSubmitAttempted(true);
    const formErrors = validateRegistrationForm(accountType, values);
    setErrors(formErrors);

    if (Object.keys(formErrors).length > 0) return;
    if (!selectedPackage) return;
    if (!usernameValid) return;
    if (!passwordsMatch) return;

    setCheckoutOpen(true);
  };

  const handleVerify = async (reference) => {
    const payload = {
      reference,
      accountType,
      packageId: selectedPackage.Id,
      fullname: values.fullname.trim(),
      username: values.username.trim(),
      email: values.email.trim(),
      password: values.password,
      ...(accountType === 'organization' && {
        orgName: values.orgName.trim(),
        orgEmail: values.orgEmail.trim(),
      }),
    };

    const response = await verifyRegistration(payload);
    setCheckoutOpen(false);
    setResult({ accountType, username: values.username.trim(), email: values.email.trim() });
    return response;
  };

  if (result) {
    return (
      <section className="container-page py-24">
        <SuccessScreen accountType={result.accountType} username={result.username} email={result.email} />
      </section>
    );
  }

  return (
    <section className="py-16 sm:py-20">
      <div className="container-page grid grid-cols-1 gap-12 lg:grid-cols-[1fr_1.2fr]">
        <div className="flex flex-col gap-6">
          <div>
            <span className="section-eyebrow">Get started</span>
            <h1 className="mt-4 text-3xl font-extrabold tracking-tight text-ink dark:text-white sm:text-4xl">
              Create your Piua AI account
            </h1>
            <p className="mt-3 text-sm leading-relaxed text-muted dark:text-subtle">
              Every message is automatically screened for sensitive information before it reaches the AI model — no
              extra steps on your part.
            </p>
          </div>

          <AccountTypeToggle value={accountType} onChange={handleAccountTypeChange} />

          <div className="flex flex-col gap-3">
            <h2 className="text-sm font-semibold text-ink dark:text-white">Choose a plan</h2>
            <PlanPicker packages={packages} selectedId={selectedPackage?.Id} onSelect={setSelectedPackage} />
          </div>
        </div>

        <div className="card flex flex-col gap-6 p-8">
          <h2 className="text-lg font-bold text-ink dark:text-white">Your details</h2>

          <RegistrationForm
            accountType={accountType}
            values={values}
            onChange={handleFieldChange}
            errors={errors}
            onValidityChange={handleUsernameValidity}
          />

          {submitAttempted && !canContinue && Object.keys(errors).length === 0 && (
            <div className="flex items-center gap-2 rounded-xl border border-warn/30 bg-warn/5 p-3 text-xs text-warn">
              <FiAlertCircle size={14} />
              Please choose a plan, pick an available username, and make sure your passwords match.
            </div>
          )}

          <button type="button" onClick={handleContinue} className="btn-primary w-full">
            Continue to payment <FiArrowRight />
          </button>

          <p className="text-center text-xs text-subtle">
            By continuing you'll be asked to complete a secure payment with Paystack before your account is created.
          </p>
        </div>
      </div>

      <CheckoutModal
        open={checkoutOpen}
        onClose={() => setCheckoutOpen(false)}
        pkg={selectedPackage}
        accountType={accountType}
        email={values.email.trim()}
        onVerify={handleVerify}
      />
    </section>
  );
}
